/**
 * AdvisorSnapshotCard Component
 * 
 * Summary card showing advisor name, AEI badge, and trend.
 * Respects visibility rules (Tweak #1): managers see full AEI, self-view sees trends only.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { TrendingUp, TrendingDown, Minus, User, Sparkles } from 'lucide-react';
import type { AdvisorIntelligenceStats } from '@/hooks/useAdvisorIntelligenceStats';

interface AdvisorSnapshotCardProps {
  profile: {
    id: string;
    display_name: string | null;
    email: string | null;
  };
  stats: AdvisorIntelligenceStats | null | undefined;
  isSelfView: boolean;
}

export function AdvisorSnapshotCard({ profile, stats, isSelfView }: AdvisorSnapshotCardProps) {
  const displayName = profile.display_name || profile.email || 'Advisor';
  const initials = displayName.slice(0, 2).toUpperCase();

  const getTrendIcon = () => {
    if (!stats?.trendDirection) return <Minus className="h-4 w-4 text-muted-foreground" />;
    switch (stats.trendDirection) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'drifting':
        return <TrendingDown className="h-4 w-4 text-amber-500" />;
      default:
        return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTrendLabel = () => {
    if (!stats?.trendDirection) return 'No data yet';
    switch (stats.trendDirection) {
      case 'improving':
        return 'Improving';
      case 'drifting':
        return 'Needs attention';
      default:
        return 'Stable';
    }
  };

  const getSampleStatusBadge = () => {
    if (!stats?.sampleStatus) return null;
    const colors = {
      learning: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      established: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      expert: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    };
    return (
      <Badge variant="secondary" className={colors[stats.sampleStatus]}>
        {stats.sampleStatus === 'learning' ? 'Building Profile' : 
         stats.sampleStatus === 'established' ? 'Established' : 'Expert'}
      </Badge>
    );
  };

  const getAEIColor = (aei: number) => {
    if (aei >= 75) return 'text-green-600 dark:text-green-400';
    if (aei >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-lg">
              {initials}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xl">{displayName}</CardTitle>
              <Badge variant="outline" className="text-xs">
                <User className="h-3 w-3 mr-1" />
                Service Advisor
              </Badge>
            </div>
            
            <div className="flex items-center gap-3 mt-2">
              {getSampleStatusBadge()}
              
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                {getTrendIcon()}
                <span>{getTrendLabel()}</span>
              </div>
            </div>
          </div>

          {/* AEI Score - Only shown to managers (not self-view) */}
          {!isSelfView && stats?.advisorEffectivenessIndex != null && (
            <div className="text-center">
              <div className="text-xs text-muted-foreground mb-1">
                Effectiveness Index
              </div>
              <div className={`text-3xl font-bold ${getAEIColor(stats.advisorEffectivenessIndex)}`}>
                {Math.round(stats.advisorEffectivenessIndex)}
              </div>
            </div>
          )}

          {/* Self-view sees trend summary instead of raw score */}
          {isSelfView && stats && (
            <div className="text-center px-4 py-2 bg-muted/50 rounded-lg">
              <Sparkles className="h-5 w-5 mx-auto mb-1 text-primary" />
              <div className="text-xs text-muted-foreground">
                Your Progress
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      {stats?.sampleCount != null && stats.sampleCount > 0 && (
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">
            Based on {stats.sampleCount} ticket{stats.sampleCount !== 1 ? 's' : ''} analyzed
          </p>
        </CardContent>
      )}
    </Card>
  );
}
